import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { motion } from 'framer-motion';
import { FilterIcon, HomeIcon, LoaderIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { SubpageNavbar } from '../components/SubpageNavbar';
import { Unit, formatArea } from '../data/units';
import { useUnits } from '../hooks/useUnits';
type FilterStatus = 'all' | 'k dispozici' | 'blokováno' | 'prodáno' | 'rezervováno';
type FilterKategorie = 'all' | '1+kk' | '2+kk' | '3+kk' | '4+kk';
type FilterPatro = 'all' | '1NP' | '2NP' | '3NP';
export function UnitsPage() {
  const {
    units,
    isLoading,
    error
  } = useUnits();
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [kategorieFilter, setKategorieFilter] = useState<FilterKategorie>('all');
  const [patroFilter, setPatroFilter] = useState<FilterPatro>('all');
  const [iframeHeight, setIframeHeight] = useState(800);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Listen for messages from the iframe to auto-resize
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === 'https://static.svatyvaclav.living' && event.data?.type === 'resize') {
        setIframeHeight(event.data.height);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  const filteredUnits = units.filter(unit => {
    if (statusFilter !== 'all' && unit.stav !== statusFilter) return false;
    if (kategorieFilter !== 'all' && unit.kategorie !== kategorieFilter) return false;
    if (patroFilter !== 'all' && unit.patro !== patroFilter) return false;
    return true;
  });
  const getStatusBadge = (unit: Unit) => {
    switch (unit.stav) {
      case 'k dispozici':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-id="element-325" data-mp="3qsxqojpi8p3c">
            {unit.cenaSDph !== null && unit.cenaSDph !== undefined ? 'K prodeji dnes' : 'K dispozici'}
          </span>;
      case 'blokováno':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800" data-id="element-326" data-mp="20px5laikvazw">
            Prodáno
          </span>;
      case 'rezervováno':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800" data-id="element-327" data-mp="180gh2ny2we8y">
            Rezervováno
          </span>;
      case 'prodáno':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800" data-id="element-328" data-mp="1hq9jaycbzh0j">
            Prodáno
          </span>;
    }
  };
  const handleContactClick = (unitKod: string) => {
    const message = `Dobrý den, mám zájem o byt ${unitKod}. Prosím o zaslání více informací.`;
    window.location.hash = `kontakt?byt=${encodeURIComponent(unitKod)}&zprava=${encodeURIComponent(message)}`;
  };
  return <div className="min-h-screen bg-base-cream font-body text-base-dark flex flex-col" data-id="element-329" data-mp="2vy8x0cpmr4jz">
      <SubpageNavbar currentPage="nabidka-bytu" data-id="element-330" data-mp="30pqzx6b7rf7l" />

      <main className="flex-grow py-16" data-id="element-331" data-mp="30qikzn76pujm">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-332" data-mp="2my2ol58sguro">
          {/* Header */}
          <div className="text-center mb-12" data-id="element-333" data-mp="2qze5mmdlk7o9">
            <h1 className="font-heading text-4xl md:text-5xl text-primary mb-4" data-id="element-334" data-mp="2r07fhw7urdam">
              Nabídka bytů
            </h1>
            <p className="text-base-gray max-w-2xl mx-auto" data-id="element-335" data-mp="3eprxabs9s4ib">
              Kompletní přehled všech bytových jednotek v projektu Svatý Václav.
            </p>
          </div>
        </div>

        {/* External Floor Plan Widget - Full width breakout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-10 overflow-hidden hidden md:block" data-id="element-336" data-mp="3amvkj1pev2ma">
          <div style={{
          position: 'relative',
          width: '100%',
          paddingBottom: '56.18%'
        }} data-id="element-337" data-mp="2ei53gdibao6i">
            <iframe ref={iframeRef} src="https://static.svatyvaclav.living/" title="Interaktivní výběr bytů – Svatý Václav" className="border-0" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflowX: 'hidden'
          }} scrolling="no" allow="fullscreen" loading="lazy" data-id="element-338" data-mp="24s1zji9njpte" />
          </div>
        </motion.div>

        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-339" data-mp="2jgopsf0ti1ze">
          {/* Filters */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-340" data-mp="1d2h0laar49ay">
            <div className="flex items-center gap-2 mb-4" data-id="element-341" data-mp="3spuo1vf9rog1">
              <FilterIcon className="w-5 h-5 text-primary" data-id="element-342" data-mp="3so9ti5syg5i6" />
              <span className="font-medium text-base-dark" data-id="element-343" data-mp="1boxztq4712v">Filtrovat</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-344" data-mp="6ai8ree0odzc">
              <div data-id="element-345" data-mp="6ccl44b82xrt">
                <label className="block text-sm text-base-gray mb-2" data-id="element-346" data-mp="b9cuop7a6ino">
                  Stav
                </label>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as FilterStatus)} className="w-full bg-base-cream border border-gray-200 rounded-lg px-4 py-2.5 text-base-dark focus:outline-none focus:border-primary transition-colors" data-id="element-347" data-mp="g7wi6u1iw2i5">
                  <option value="all" data-id="element-348" data-mp="3pca924o5fcu6">Všechny</option>
                  <option value="k dispozici" data-id="element-349" data-mp="3pd39yp0ff84e">K dispozici</option>
                  <option value="blokováno" data-id="element-350" data-mp="3kdqjzve9dkxl">Blokováno</option>
                  <option value="rezervováno" data-id="element-351" data-mp="3kfbczgkxcvtc">Rezervováno</option>
                  <option value="prodáno" data-id="element-352" data-mp="28djg7kha9k3">Prodáno</option>
                </select>
              </div>
              <div data-id="element-353" data-mp="3ualkgju3dvei">
                <label className="block text-sm text-base-gray mb-2" data-id="element-354" data-mp="3ua3g0zxa0avi">
                  Dispozice
                </label>
                <select value={kategorieFilter} onChange={e => setKategorieFilter(e.target.value as FilterKategorie)} className="w-full bg-base-cream border border-gray-200 rounded-lg px-4 py-2.5 text-base-dark focus:outline-none focus:border-primary transition-colors" data-id="element-355" data-mp="76ov8nuc2u4y">
                  <option value="all" data-id="element-356" data-mp="uu6sv00a4mfu">Všechny</option>
                  <option value="1+kk" data-id="element-357" data-mp="py7x2rcmg25e">1+kk</option>
                  <option value="2+kk" data-id="element-358" data-mp="pvbz530rxdgy">2+kk</option>
                  <option value="3+kk" data-id="element-359" data-mp="19jzxc1ftlzdy">3+kk</option>
                  <option value="4+kk" data-id="element-360" data-mp="1ei1icrwt48s2">4+kk</option>
                </select>
              </div>
              <div data-id="element-361" data-mp="1efp34mifocip">
                <label className="block text-sm text-base-gray mb-2" data-id="element-362" data-mp="10ijxuoqjnhoi">
                  Podlaží
                </label>
                <select value={patroFilter} onChange={e => setPatroFilter(e.target.value as FilterPatro)} className="w-full bg-base-cream border border-gray-200 rounded-lg px-4 py-2.5 text-base-dark focus:outline-none focus:border-primary transition-colors" data-id="element-363" data-mp="14iu6681t6k6j">
                  <option value="all" data-id="element-364" data-mp="lt4cjo9s84g7">Všechny</option>
                  <option value="1NP" data-id="element-365" data-mp="ltxe8atvw3rh">1. NP</option>
                  <option value="2NP" data-id="element-366" data-mp="7t30dco2half">2. NP</option>
                  <option value="3NP" data-id="element-367" data-mp="7ueea5b87yq9">3. NP</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Results count */}
          <div className="mb-6 text-base-gray" data-id="element-368" data-mp="s9nyvcodiscb">
            Nalezeno{' '}
            <strong className="text-base-dark" data-id="element-369" data-mp="nbvzjxleg3y3">{filteredUnits.length}</strong>{' '}
            bytů
          </div>

          {/* Table - Desktop */}
          <div className="hidden lg:block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" data-id="element-370" data-mp="iayijoc4lfy6">
            <table className="w-full" data-id="element-371" data-mp="ed0oo87eu5og">
              <thead data-id="element-372" data-mp="9cd2bzu4md0k">
                <tr className="bg-primary text-white" data-id="element-373" data-mp="9cwgr6kkij9u">
                  <th className="px-4 py-4 text-left font-medium text-sm" data-id="element-374" data-mp="4l4s51kz2nmc">
                    Kód
                  </th>
                  <th className="px-4 py-4 text-left font-medium text-sm" data-id="element-375" data-mp="3vqwbni48v206">
                    Podlaží
                  </th>
                  <th className="px-4 py-4 text-left font-medium text-sm" data-id="element-376" data-mp="3qtnspfb9utxv">
                    Dispozice
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-377" data-mp="3msu56n1qrh5i">
                    Celková plocha
                    <br data-id="element-378" data-mp="3hu0v74lny8a3" />
                    jednotky
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-379" data-mp="1x9cix8p1tg3h">
                    Podlahová plocha
                    <br data-id="element-380" data-mp="22ajp53bjm781" />
                    bytu
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-381" data-mp="26atse87yajj7">
                    Terasa
                  </th>
                  <th className="px-4 py-4 text-right font-medium text-sm" data-id="element-382" data-mp="2b712md7dqh6y">
                    Předzahrádka
                  </th>
                  <th className="px-4 py-4 text-center font-medium text-sm" data-id="element-383" data-mp="1ik7yrlyp11sq">
                    Stav
                  </th>
                  <th className="px-4 py-4 text-center font-medium text-sm" data-id="element-384" data-mp="1nj0zsewsn0c0">
                    Cena
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-385" data-mp="uvf0vjyigjvh">
                {filteredUnits.map((unit, index) => {
                const hasPrice = unit.cenaSDph !== null && unit.cenaSDph !== undefined;
                const textColor = hasPrice ? 'text-base-dark' : 'text-gray-400';
                return <motion.tr key={unit.id} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.3,
                  delay: index * 0.02
                }} className={`border-b border-gray-100 last:border-none ${hasPrice ? 'hover:bg-base-cream/50' : 'bg-gray-50/60'}`} data-id="element-386" data-mp="1gerobnf13su2" data-mpi={index}>
                      <td className={`px-4 py-4 font-medium ${textColor}`} data-id="element-387" data-mp="16lu1j5307hpn">
                        {unit.kod}
                      </td>
                      <td className={`px-4 py-4 ${textColor}`} data-id="element-388" data-mp="1bf5b3dz7acl9">{unit.patro}</td>
                      <td className={`px-4 py-4 font-medium ${textColor}`} data-id="element-389" data-mp="dxog0g44loor">
                        {unit.kategorie}
                      </td>
                      <td className={`px-4 py-4 text-right font-medium ${textColor}`} data-id="element-390" data-mp="be1t7mxc8il6">
                        {formatArea(unit.uzitnaPlochM2 + (unit.terasaM2 ?? 0))}
                      </td>
                      <td className={`px-4 py-4 text-right ${textColor}`} data-id="element-391" data-mp="1f2kk4ckomy6">
                        {formatArea(unit.uzitnaPlochM2)}
                      </td>
                      <td className={`px-4 py-4 text-right ${textColor}`} data-id="element-392" data-mp="3sldl7zdo7loz">
                        {formatArea(unit.terasaM2)}
                      </td>
                      <td className={`px-4 py-4 text-right ${textColor}`} data-id="element-393" data-mp="3jt1xwmdused8">
                        {formatArea(unit.predzahradkaM2)}
                      </td>
                      <td className="px-4 py-4 text-center" data-id="element-394" data-mp="3erw38j0muh12">
                        {getStatusBadge(unit)}
                      </td>
                      <td className="px-4 py-4 text-center" data-id="element-395" data-mp="34wawy8oj6uli">
                        {hasPrice ? <span className="font-medium text-base-dark whitespace-nowrap" data-id="element-396" data-mp="30u7idb6kf9w0">
                            {unit.cenaSDph.toLocaleString('cs-CZ', {
                        maximumFractionDigits: 0
                      })}{' '}
                            Kč
                          </span> : unit.stav === 'k dispozici' ? <button onClick={() => handleContactClick(unit.kod)} className="text-gray-400 hover:text-gray-500 font-medium text-sm underline underline-offset-2 transition-colors" data-id="element-397" data-mp="2m7h83kav66ya">
                            Kontaktujte nás
                          </button> : <span className="text-gray-400 text-sm" data-id="element-398" data-mp="305pa46mrbygb">—</span>}
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>

          {/* Cards - Mobile/Tablet */}
          <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-399" data-mp="1jmi2wojyxey">
            {filteredUnits.map((unit, index) => <motion.div key={unit.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: index * 0.05
          }} className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 ${unit.stav === 'prodáno' ? 'opacity-70' : ''}`} data-id="element-400" data-mp="6uzxcxyu84t2" data-mpi={index}>
                <div className="flex items-start justify-between mb-4" data-id="element-401" data-mp="1wonin8jluau">
                  <div className="flex items-center gap-3" data-id="element-402" data-mp="3t282bz5ui9ok">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center" data-id="element-403" data-mp="3t418ldcjqrw1">
                      <HomeIcon className="w-5 h-5 text-primary" data-id="element-404" data-mp="1dcwsevbg5hpo" />
                    </div>
                    <div data-id="element-405" data-mp="18dtq3imupple">
                      <h3 className="font-heading text-lg font-bold text-base-dark" data-id="element-406" data-mp="3e09ix2tu0lqh">
                        {unit.kod}
                      </h3>
                      <p className="text-sm text-base-gray" data-id="element-407" data-mp="3izvz9l4yii3i">{unit.kategorie}</p>
                    </div>
                  </div>
                  {getStatusBadge(unit)}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4" data-id="element-408" data-mp="3s0kay96vo6d8">
                  <div data-id="element-409" data-mp="tzo71nuvkm0">
                    <span className="text-base-gray" data-id="element-410" data-mp="sg4t26wktg0">Podlaží:</span>
                    <span className="ml-2 font-medium text-base-dark" data-id="element-411" data-mp="5sazzhxpo56v">
                      {unit.patro}
                    </span>
                  </div>
                  <div data-id="element-412" data-mp="ak2q5rcyg8wh">
                    <span className="text-base-gray" data-id="element-413" data-mp="fie1k6iwergt">Plocha:</span>
                    <span className="ml-2 font-medium text-base-dark" data-id="element-414" data-mp="fhufcdm7tgwz">
                      {formatArea(unit.uzitnaPlochM2)}
                    </span>
                  </div>
                  {unit.terasaM2 && <div data-id="element-415" data-mp="ogzhdtwzjsdw">
                      <span className="text-base-gray" data-id="element-416" data-mp="290uupb0vuzcx">Terasa:</span>
                      <span className="ml-2 font-medium text-base-dark" data-id="element-417" data-mp="28z05vlmhqn3k">
                        {formatArea(unit.terasaM2)}
                      </span>
                    </div>}
                  {unit.predzahradkaM2 && <div data-id="element-418" data-mp="1v1v808wxkuje">
                      <span className="text-base-gray" data-id="element-419" data-mp="1v2mzb7jhw63v">Předzahrádka:</span>
                      <span className="ml-2 font-medium text-base-dark" data-id="element-420" data-mp="2rrubhoy852v4">
                        {formatArea(unit.predzahradkaM2)}
                      </span>
                    </div>}
                </div>

                {unit.stav === 'k dispozici' && <button onClick={() => handleContactClick(unit.kod)} className="w-full bg-primary hover:bg-primary-light text-white py-3 rounded-lg font-medium transition-colors" data-id="element-421" data-mp="2e0fshi4bh1o2">
                    Kontaktujte nás
                  </button>}
              </motion.div>)}
          </div>

          {filteredUnits.length === 0 && <div className="text-center py-16" data-id="element-422" data-mp="3ukge1ambij1e">
              <p className="text-base-gray text-lg" data-id="element-423" data-mp="3umaxekhxy33d">
                Žádné byty neodpovídají vybraným filtrům.
              </p>
            </div>}

          {/* Pricing Philosophy */}
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="mt-20 max-w-3xl mx-auto" aria-labelledby="cenova-politika-heading" data-id="element-424" data-mp="3ksam20w2g1m7">
            <h2 id="cenova-politika-heading" className="font-heading text-3xl md:text-4xl text-primary mb-8 text-center" data-id="element-425" data-mp="w32omyr0kgtu">
              Naše cenová politika
            </h2>

            <blockquote className="border-l-4 border-accent pl-6 mb-10 space-y-4" data-id="element-426" data-mp="2kmgp7ia7gayv">
              <p className="font-heading text-xl md:text-2xl text-base-dark italic leading-relaxed" data-id="element-427" data-mp="2gm6ig3el9giy">
                „Mám velmi jednoduchý vkus. Spokojím se vždy s tím nejlepším.“
                <span className="block mt-2 text-base text-base-gray not-italic font-body" data-id="element-428" data-mp="2pk8mkrtmcpag">
                  — Oscar Wilde
                </span>
              </p>
              <p className="font-heading text-xl md:text-2xl text-base-dark italic leading-relaxed" data-id="element-429" data-mp="2zbd33vyy0i7e">
                „Nejsem tak bohatý, abych si mohl kupovat levné věci.“
                <span className="block mt-2 text-base text-base-gray not-italic font-body" data-id="element-430" data-mp="3d8rv98gkz432">
                  — Tomáš Baťa
                </span>
              </p>
            </blockquote>

            <div className="space-y-5 text-base-gray text-lg leading-relaxed" data-id="element-431" data-mp="1ix3d6xf1yleg">
              <p data-id="element-432" data-mp="1dz1uidljgo3i">
                Oba pánové, stejně jako my, dobře věděli, co je podstatou
                skutečně kvalitních věcí a proč nemá smysl šetřit na nesprávném
                místě.
              </p>
              <p data-id="element-433" data-mp="x24f4nnh38xi">
                Nemovitost je investice s životností více než 100 let a její
                kvalita je dána jak funkčností a designem, tak i výší provozních
                nákladů – měsíčními platbami za energie, vodné, fond oprav a
                další režií spojenou s užíváním.
              </p>
              <p data-id="element-434" data-mp="a5kr6clnr7td">
                Pokud je nemovitost splácaná z levných a nekvalitních komponentů
                a materiálů s krátkou životností, má vysoké provozní náklady a
                brzy potřebuje nákladné opravy, tak je ve své podstatně velmi
                drahá a tato kategorie není naším oborem.
              </p>
              <p data-id="element-435" data-mp="1ujl18w0kkmhv">
                Dům U Sv. Václava patří do nejvyšší kategorie staveb reálné
                nadčasové hodnoty se značnou variabilitou finálního provedení, a
                proto jsme připraveni věnovat svůj čas k nalezení optimálního
                řešení reálným zájemcům, kteří hledají kvalitní prostor pro
                spokojený život.
              </p>
              <p data-id="element-436" data-mp="3aadtktdr9scb">
                Konečné ceny jednotlivých bytů v našem projektu jsou výsledkem
                cenové kalkulace konkrétního bytového prostoru ve stavu, o
                kterém si rozhoduje každý klient sám a tato cena není informací
                veřejnou.
              </p>
              <p className="font-medium text-base-dark" data-id="element-437" data-mp="2wama4d4gzjqx">
                Zájemcům, či zprostředkovatelům, kteří hledají levný byt, nebo
                jen „věc“ ke spekulativnímu nákupu nemáme co nabídnout.
              </p>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer data-id="element-438" data-mp="2met133pym8dw" />
    </div>;
}